import { CSSObject } from '@emotion/react';
import {
    useHoneycombTheme,
    NotificationColor,
    HoneycombTheme,
    NotificationElementColors,
    getSizeValue,
    NotificationCornerStyle,
    ResponsiveStyleValue,
    createResponsiveStyles,
    NotificationVariant,
    mergeResponsiveStyleValues,
    HoneycombShadowSize,
} from '../../theming';

const getElementColors = (color: NotificationColor, theme: HoneycombTheme): NotificationElementColors => {
    const { palette, components } = theme;
    const componentTheme = components.themes.Notification;
    const { elementShades } = componentTheme;

    // This is a `system` color
    const systemColor = palette.system[color];
    return {
        background: systemColor[elementShades.background],
        border: systemColor[elementShades.border],
        icon: systemColor[elementShades.icon],
        text: theme.fn.resolveColorValue(componentTheme.textColor),
    };
};

function getResponsiveStyles(theme: HoneycombTheme) {
    return (value: { variant: NotificationVariant; cornerStyle: NotificationCornerStyle }): CSSObject => {
        const { variant, cornerStyle } = value;
        const variantTheme = theme.components.themes.Notification.variant[variant];
        const {
            radius,
            padding,
            iconSize: iconSizeProp,
            iconClearance,
            dismissButtonClearance,
            fontSize,
            contentSpacing,
        } = variantTheme;
        const typography = theme.typography.variant.body[fontSize].l;
        const iconSize = getSizeValue(iconSizeProp);

        return {
            borderRadius: cornerStyle === 'rounded' ? getSizeValue(radius) : undefined,
            gap: getSizeValue(dismissButtonClearance),
            padding: `${getSizeValue(padding.y)} ${getSizeValue(padding.x)}`,
            fontSize: typography.fontSize,
            lineHeight: typography.lineHeight,

            '[data-icon="true"]': {
                width: iconSize,
                height: iconSize,
            },

            '[data-inner="true"]': {
                gap: getSizeValue(iconClearance),
            },

            '[data-content="true"]': {
                gap: getSizeValue(contentSpacing),
            },
        };
    };
}

export interface NotificationStylesParams {
    color: NotificationColor;
    cornerStyle: ResponsiveStyleValue<NotificationCornerStyle>;
    variant: ResponsiveStyleValue<NotificationVariant>;
    shadow: HoneycombShadowSize;
}

type NotificationStyleClasses = 'root' | 'icon' | 'inner' | 'content';

export function useStyles({ color, cornerStyle, variant, shadow: shadowProp }: NotificationStylesParams): {
    styles: { [key in NotificationStyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();

    const componentTheme = theme.components.themes.Notification;
    const { borderWidth } = componentTheme;

    const colors = theme.fn.resolveFlatColorObject(getElementColors(color, theme));
    const shadow = shadowProp ? theme.shadows[shadowProp] : shadowProp;

    const root: CSSObject = {
        display: 'flex',
        borderStyle: 'solid',
        borderWidth: getSizeValue(borderWidth),
        ...createResponsiveStyles(
            [
                {
                    value: mergeResponsiveStyleValues({ variant, cornerStyle }, theme),
                    getStyles: getResponsiveStyles(theme),
                },
            ],
            theme
        ),
        backgroundColor: colors.background,
        color: colors.text,
        borderColor: colors.border,
        boxShadow: shadow,
    };

    const icon: CSSObject = {
        flexShrink: 0,
        color: colors.icon,
    };

    const inner: CSSObject = {
        display: 'flex',
        flexGrow: 1,
        minWidth: 0, // Allow content to truncate
    };

    const content: CSSObject = {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flexGrow: 1,
        minWidth: 0, // Allow content to truncate
    };

    return {
        styles: {
            root,
            icon,
            inner,
            content,
        },
    };
}
